// Page-gate modal: shows a forced confirmation modal on configured pages until the user confirms.
// Trigger: page load on a URL listed in data-gated-urls of a ModalSection with data-modal-behavior="gate".
// On confirm: sets a cookie, hides the modal. If data-session-based="true", the cookie has no
// max-age (cleared when the browser/tab closes); otherwise max-age = data-remember-days * 86400.
// On cancel: navigates to data-deny-redirect; else history.back() when a referrer exists; else home ("/").
import { Modal } from 'bootstrap'

const COOKIE_PREFIX = 'obee.gate-'

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\\/+^]/g, '\\$&') + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  let c = `${name}=${encodeURIComponent(value)}; path=/; samesite=lax`
  if (maxAgeSeconds > 0) c += `; max-age=${maxAgeSeconds}`
  if (window.location.protocol === 'https:') c += '; secure'
  document.cookie = c
}

function normalize(path: string): string {
  if (!path) return ''
  // Strip query/hash, lowercase, trim trailing slash.
  const idx = Math.min(
    ...[path.indexOf('?'), path.indexOf('#')].filter(i => i >= 0).concat([path.length])
  )
  return path.substring(0, idx).toLowerCase().replace(/\/+$/, '')
}

document.addEventListener('DOMContentLoaded', () => {
  const gates = Array.from(document.querySelectorAll<HTMLElement>('[data-modal-behavior="gate"]'))
  if (gates.length === 0) return

  const currentPath = normalize(window.location.pathname) || '/'

  for (const el of gates) {
    let urls: string[] = []
    try {
      urls = JSON.parse(el.getAttribute('data-gated-urls') || '[]')
    } catch {
      urls = []
    }
    if (urls.length === 0) continue

    const matches = urls.some(u => {
      const norm = normalize(u) || '/'
      return norm === currentPath
    })
    if (!matches) continue

    const id = el.id || 'default'
    const cookieName = COOKIE_PREFIX + id
    if (readCookie(cookieName) === '1') continue

    const sessionBased = el.getAttribute('data-session-based') === 'true'
    const rememberDays = Math.max(0, parseInt(el.getAttribute('data-remember-days') || '0', 10) || 0)
    const denyRedirect = el.getAttribute('data-deny-redirect') || ''
    const modal = Modal.getOrCreateInstance(el)
    modal.show()

    el.querySelectorAll<HTMLElement>('[data-modal-action="confirm"]').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.preventDefault()
        if (sessionBased) {
          writeCookie(cookieName, '1', 0)
        } else if (rememberDays > 0) {
          writeCookie(cookieName, '1', rememberDays * 86400)
        }
        modal.hide()
      })
    })
    el.querySelectorAll<HTMLElement>('[data-modal-action="cancel"]').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.preventDefault()
        if (denyRedirect) {
          window.location.href = denyRedirect
        } else if (document.referrer) {
          window.history.back()
        } else {
          // No configured redirect and no history (e.g. URL typed directly) — send home.
          window.location.href = '/'
        }
      })
    })

    // Only one gate per page load.
    break
  }
})
